/**
 * Build mode. `vite build --mode embedded` (the `build:local` script; Vite reserves "local") produces the copy rago-server embeds
 * and serves at `http://<lan-ip>:41400/web/` (Plex's server-hosted web app):
 * same-origin media server, plain HTTP, this server only. The flag is a
 * build-time constant so hosted-only code tree-shakes out of that bundle
 * and local-only code out of app.rago.tv's.
 */
export const isLocalMode: boolean = import.meta.env.MODE === "embedded";

/** "" hosted, "/web" local — the router's basepath. */
export const BASE_PATH: string = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");

/** window.location.pathname → app path. The router already strips the
 * basepath from its own `location.pathname`; this is for raw window reads. */
export function stripBase(pathname: string, base: string = BASE_PATH): string {
  if (!base) return pathname;
  if (pathname === base) return "/";
  return pathname.startsWith(base + "/") ? pathname.slice(base.length) : pathname;
}

/** Cloud-only surfaces the local (this-server-only) app does not offer. */
const HOSTED_ONLY: RegExp[] = [
  /^\/discover(\/|$)/,
  /^\/watchlist(\/|$)/,
  /^\/profile(\/|$)/,
  /^\/claim(\/|$)/,
  /^\/link(\/|$)/,
  /^\/settings\/(account|devices|sharing|security|trakt)(\/|$)/,
];

export function isHostedOnlyPath(pathname: string): boolean {
  return HOSTED_ONLY.some((re) => re.test(pathname));
}

/** The sign-in `next` target for a router location: pathname plus query
 * (a `/link?code=…` deep link must survive the sign-in round trip); the
 * home path yields undefined so the URL stays clean. */
export function nextFromLocation(l: { pathname: string; searchStr?: string }): string | undefined {
  if (l.pathname === "/" && !l.searchStr) return undefined;
  return `${l.pathname}${l.searchStr ?? ""}`;
}

/** A search flag such as `?popup=1`. The router JSON-parses search values,
 * so it arrives as 1, "1" or true. */
export function isSearchFlag(v: unknown): boolean {
  return v === true || String(v) === "1";
}

/** /link as the server-hosted app's approval page — in the popup it opened
 * (`popup=1`) or in its own tab sent over (`redirect=1`, lib/linkFlow
 * pinRedirectUrl): no app bar, and a sign-out comes back to the request
 * (routes/_authed.tsx). */
export function isLinkApproval(l: { pathname: string; search: unknown }): boolean {
  if (l.pathname !== "/link") return false;
  const search = (l.search ?? {}) as { popup?: unknown; redirect?: unknown };
  return isSearchFlag(search.popup) || isSearchFlag(search.redirect);
}

/** /claim/approve/<code>, the setup wizard's consent page. Only ever reached
 * from a server's /setup — its window, or its link opened by hand — so it
 * needs no flag to be one. */
export function isClaimApproval(l: { pathname: string }): boolean {
  return /^\/claim\/approve\/[^/]+\/?$/.test(l.pathname);
}

/** The approval pages: the bare frame (no app bar, no search, no server
 * sockets — routes/_authed.tsx), and a sign-out comes back to the request. */
export function isBareApproval(l: { pathname: string; search: unknown }): boolean {
  return isLinkApproval(l) || isClaimApproval(l);
}
